import { useCallback, useEffect, useMemo } from 'react';

import { ExportQueryFn, useExportQuery } from '@givebacks/api/query';
import { useAtomValue } from 'jotai';
import { Menu } from 'ui';

import { useTablePdf } from '../../../pdf/useTablePdf';
import { useTableContext } from '../../../table.context';
import { TableColumns } from '../../../types/tableColumns.types';
import { TablePdfOptions } from '../../../types/tablePdf.types';

type TablePDfExportType = 'save' | 'print';

const useTablePdfExport = <T extends ExportQueryFn<any>>({
  pdfOptions,
  exportQuery,
  columns,
  type,
}: {
  exportQuery: T;
  columns: TableColumns<unknown>;
  pdfOptions: TablePdfOptions;
  type: TablePDfExportType;
}) => {
  const ctx = useTableContext();
  const querySettings = ctx.exportOptions?.settings;

  const searchObject = useAtomValue(ctx.tableSearchObjectAtom, {
    store: ctx.atomStore,
  });

  const { startExport, exporting, isFetching, formatExportData } = useExportQuery({
    useExportFn: exportQuery,
    queryOptions: searchObject,
    settings: querySettings,
  });

  const { handleStart, cleanupTargetFrame, isLoading } = useTablePdf({
    columns,
    ...pdfOptions,
    pdfAction: type == 'print' ? 'print' : 'download',
  });

  const handlePrintTable = useCallback(
    async <TData extends Array<unknown>>(data?: TData) => {
      try {
        if (!data) return;
        handleStart({ data });
      } catch {
        console.log('error');
      }
    },
    [handleStart]
  );

  const handleStartSave = useCallback(async () => {
    startExport((response) => {
      const formattedData = formatExportData(response);
      handlePrintTable(formattedData);
    });
  }, [startExport, formatExportData, handlePrintTable]);

  return {
    handleStartSave,
    exporting,
    isFetching,
    isLoading,
    cleanupTargetFrame,
  };
};

const TablePDFExportMenuItem = <T extends ExportQueryFn<any>>({
  exportQuery,
  columns,
  pdfOptions,
  type,
  setIsLoading,
}: {
  exportQuery: T;
  columns: TableColumns<unknown>;
  pdfOptions: TablePdfOptions;
  type: TablePDfExportType;
  setIsLoading?: (isLoading: boolean) => void;
}) => {
  const {
    handleStartSave,
    exporting,
    isFetching,
    isLoading: isPdfLoading,
  } = useTablePdfExport({
    exportQuery,
    columns,
    pdfOptions,
    type,
  });

  const isLoading = useMemo(
    () => Boolean(exporting || isFetching || isPdfLoading),
    [exporting, isFetching, isPdfLoading]
  );

  useEffect(() => {
    setIsLoading?.(!!isLoading);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Don't run on fn change
  }, [isLoading]);

  return (
    <Menu.Item
      key={'pdf-export'}
      onClick={(e) => {
        e.stopPropagation();
        return handleStartSave();
      }}
      disabled={isLoading}
    >
      {type === 'save' ? 'Export as PDF' : 'Print'}
    </Menu.Item>
  );
};

const TablePDF = ({
  type,
  setIsLoading,
}: {
  type: TablePDfExportType;
  setIsLoading?: (isLoading: boolean) => void;
}) => {
  const ctx = useTableContext();
  const exportQuery = ctx.exportOptions?.query;
  const columns = ctx.columns as TableColumns<unknown>;
  const pdfOptions = useMemo(() => {
    const pdfOptions = ctx.pdfOptions;
    if (!pdfOptions) return;
    return pdfOptions;
  }, [ctx.pdfOptions]);

  if (!exportQuery || !columns || !pdfOptions) return null;
  return (
    <TablePDFExportMenuItem
      exportQuery={exportQuery}
      columns={columns}
      pdfOptions={pdfOptions}
      type={type}
      setIsLoading={setIsLoading}
    />
  );
};

export { TablePDF };
